// Frontend console bridge. Patches console.* so every error/warn/info/debug
// call in the React app is mirrored to the unified log.
// Best-effort: any failure here is silent (don't break the app for logging).
//
// Mounted once from src/main.tsx before the React tree renders.

import { api } from "./services"

let installed = false

export function installConsoleBridge() {
  if (installed) return
  installed = true

  const orig = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug.bind(console),
  }

  function stringify(arg: unknown): string {
    if (arg instanceof Error) return `${arg.message}${arg.stack ? "\n" + arg.stack : ""}`
    if (typeof arg === "string") return arg
    try {
      return JSON.stringify(arg)
    } catch {
      return String(arg)
    }
  }

  function post(level: "error" | "warn" | "info" | "debug", args: unknown[]) {
    try {
      const msg = args.map(stringify).join(" ")
      // Keep meta small; large objects would bloat the log file.
      const meta: Record<string, unknown> = { origin: "frontend" }
      if (typeof window !== "undefined" && window.location) meta.path = window.location.pathname
      void api.log.post({ level, source: "frontend", msg: msg.slice(0, 4000), meta })
    } catch {
      /* never throw from a log path */
    }
  }

  console.log = (...args: unknown[]) => { orig.log(...args); post("info", args) }
  console.info = (...args: unknown[]) => { orig.info(...args); post("info", args) }
  console.warn = (...args: unknown[]) => { orig.warn(...args); post("warn", args) }
  console.error = (...args: unknown[]) => { orig.error(...args); post("error", args) }
  console.debug = (...args: unknown[]) => { orig.debug(...args); post("debug", args) }

  // Window-level error handlers catch what console.* does not (an unhandled
  // rejection before any caller logged it).
  if (typeof window !== "undefined") {
    window.addEventListener("error", (e) => {
      post("error", [`window.error: ${e.message}`, e.error ?? e.filename + ":" + e.lineno])
    })
    window.addEventListener("unhandledrejection", (e) => {
      const reason = e.reason instanceof Error ? `${e.reason.message}\n${e.reason.stack ?? ""}` : String(e.reason)
      post("error", [`unhandledrejection: ${reason}`])
    })
  }
}
